import React from 'react';
import { Box, Checkbox, useTheme } from '@mui/material';
import dayjs from 'dayjs';

import { Button, DatePicker, Select, SVGButton, Text, TextField } from 'components';
import { formatDate } from 'core/shared';
import { useToast } from 'core/toast/hooks';
import { useHasMobileView } from 'hooks/useHasMobileView';
import { useTranslations } from 'hooks/useTranslations';

import { UseMutation, UseQuery } from 'useQuery';
import { ScheduledItem, ScheduledItemBody } from 'useQuery/types';

import { NONE_VAT, SectionSaveFooter, useDefaultVatId, useVatOptions } from './InvoicingShared';

interface OrganisationScheduledItemsProps {
  organisationId: string;
}

interface EditableRow {
  key: string;
  id: string | null;
  description: string;
  amount: number; // euros, signed; negative = credit
  vatId: string; // NONE_VAT or a VAT id
  invoiceMonth: string; // any date within the selected invoice month (picker value)
  countsAsRevenue: boolean; // false = pass-through cost
  processedOn: string | null;
}

// One-time fees are booked directly onto the chosen month: the invoice is
// generated on the 1st of that same month, no shift applied. dayjs re-localises
// the picker's UTC ISO string first, so month boundaries land on the right
// calendar month.
function invoiceOnFromMonth(monthValue: string): string {
  if (!monthValue || !dayjs(monthValue).isValid()) {
    return '';
  }
  return dayjs(monthValue).startOf('month').format('YYYY-MM-DD');
}

function OrganisationScheduledItems({ organisationId }: OrganisationScheduledItemsProps) {
  const theme = useTheme();
  const { hasMobileView } = useHasMobileView();
  const translations = useTranslations('organisationInvoicing');
  const { addToast } = useToast();

  const { data: items, refetch } = UseQuery.scheduledItems({ id: organisationId });
  const vatOptions = useVatOptions();
  const defaultVatId = useDefaultVatId();
  const { mutateAsync: createItem } = UseMutation.createScheduledItem();
  const { mutateAsync: updateItem } = UseMutation.updateScheduledItem();
  const { mutateAsync: deleteItem } = UseMutation.deleteScheduledItem();

  const [rows, setRows] = React.useState<EditableRow[]>([]);
  const [saving, setSaving] = React.useState(false);
  const keyCounter = React.useRef(0);

  const serverRows = React.useMemo(() => items ?? [], [items]);

  function itemToRow(item: ScheduledItem): EditableRow {
    return {
      key: `srv-${item.id}`,
      id: item.id,
      description: item.description,
      amount: item.amount_net / 100,
      vatId: item.vat_id ?? NONE_VAT,
      invoiceMonth: item.invoice_on,
      countsAsRevenue: item.counts_as_revenue,
      processedOn: item.processed_on,
    };
  }

  React.useEffect(() => {
    setRows(serverRows.map(itemToRow));
  }, [serverRows]);

  function updateRow(key: string, patch: Partial<EditableRow>) {
    setRows((prev) => prev.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  }

  function addRow() {
    keyCounter.current += 1;
    setRows((prev) => [
      ...prev,
      {
        key: `new-${keyCounter.current}`,
        id: null,
        description: '',
        amount: 0,
        vatId: defaultVatId,
        invoiceMonth: '',
        countsAsRevenue: true,
        processedOn: null,
      },
    ]);
  }

  function removeRow(key: string) {
    setRows((prev) => prev.filter((row) => row.key !== key));
  }

  function rowToBody(row: EditableRow): ScheduledItemBody {
    return {
      description: row.description.trim(),
      amount_net: Math.round(row.amount * 100),
      invoice_on: invoiceOnFromMonth(row.invoiceMonth),
      vat_id: row.vatId === NONE_VAT ? null : row.vatId,
      counts_as_revenue: row.countsAsRevenue,
    };
  }

  function bodyEquals(row: EditableRow, item: ScheduledItem): boolean {
    const body = rowToBody(row);
    return (
      body.description === item.description &&
      body.amount_net === item.amount_net &&
      body.invoice_on === item.invoice_on &&
      body.vat_id === (item.vat_id ?? null) &&
      body.counts_as_revenue === item.counts_as_revenue
    );
  }

  // A row is "meaningful" once it has any content; empty new rows are ignored.
  const nonEmptyRows = rows.filter(
    (row) =>
      !row.processedOn &&
      (row.description.trim() !== '' || row.amount !== 0 || row.invoiceMonth !== ''),
  );

  const diff = React.useMemo(() => {
    const serverById = new Map(serverRows.map((item) => [item.id, item]));
    const toCreate: EditableRow[] = [];
    const toUpdate: EditableRow[] = [];
    rows.forEach((row) => {
      if (row.processedOn) {
        return;
      }
      if (!row.id) {
        if (row.description.trim() !== '' || row.amount !== 0 || row.invoiceMonth !== '') {
          toCreate.push(row);
        }
        return;
      }
      const server = serverById.get(row.id);
      if (server && !bodyEquals(row, server)) {
        toUpdate.push(row);
      }
    });
    const localIds = new Set(rows.map((row) => row.id).filter(Boolean));
    const toDelete = serverRows.filter((item) => !item.processed_on && !localIds.has(item.id));
    return { toCreate, toUpdate, toDelete };
  }, [rows, serverRows]);

  const hasChanges =
    diff.toCreate.length > 0 || diff.toUpdate.length > 0 || diff.toDelete.length > 0;
  const missingFields = nonEmptyRows.some(
    (row) => row.description.trim() === '' || invoiceOnFromMonth(row.invoiceMonth) === '',
  );

  async function save() {
    if (missingFields) {
      addToast({ title: translations.itemFieldsRequired, severity: 'error' });
      return;
    }
    setSaving(true);
    try {
      for (const row of diff.toDelete) {
        await deleteItem({ id: organisationId, itemId: row.id });
      }
      for (const row of diff.toUpdate) {
        await updateItem({ id: organisationId, itemId: row.id as string, item: rowToBody(row) });
      }
      for (const row of diff.toCreate) {
        await createItem({ id: organisationId, item: rowToBody(row) });
      }
      await refetch();
      addToast({ title: translations.itemsSaved, severity: 'success' });
    } catch (error) {
      // per-request onError toast already fired
    } finally {
      setSaving(false);
    }
  }

  return (
    <Box
      sx={{
        backgroundColor: 'background.paper',
        padding: 2,
        marginBottom: 2,
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        maxWidth: `calc(100vw - ${theme.spacing(hasMobileView ? 4 : 6)})`,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 2,
          paddingBottom: 1,
        }}
      >
        <Box>
          <Text.Bold>{translations.oneTimeTitle}</Text.Bold>
          <Box sx={{ paddingTop: 0.5 }}>
            <Text.Small color="gray">{translations.oneTimeSubtitle}</Text.Small>
          </Box>
        </Box>
        <Button label={translations.addItem} variant="outlined" svgName="add" onClick={addRow} />
      </Box>

      {rows.length === 0 && (
        <Box sx={{ paddingY: 2 }}>
          <Text color="gray">{translations.noOneTime}</Text>
        </Box>
      )}

      {rows.length > 0 && (
        <Box sx={{ overflowX: 'auto' }}>
          {rows.map((row) => (
            <Box
              key={row.key}
              sx={{
                display: 'flex',
                flexDirection: hasMobileView ? 'column' : 'row',
                alignItems: hasMobileView ? 'stretch' : 'center',
                gap: 1,
                paddingY: 1,
                borderBottom: 1,
                borderColor: 'divider',
                minWidth: hasMobileView ? undefined : 700,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 140 }}>
                <TextField
                  type="text"
                  label={translations.description}
                  value={row.description}
                  onChange={(value) => updateRow(row.key, { description: value })}
                  disabled={!!row.processedOn}
                  fullWidth
                />
              </Box>
              <Box sx={{ width: hasMobileView ? undefined : 120, flexShrink: 0 }}>
                <TextField
                  type="number"
                  label={translations.amount}
                  value={row.amount}
                  onChange={(value) => updateRow(row.key, { amount: value })}
                  disabled={!!row.processedOn}
                  adornment="EUR"
                  fullWidth
                />
              </Box>
              <Box sx={{ width: hasMobileView ? undefined : 125, flexShrink: 0 }}>
                <Select
                  mode="single"
                  label={translations.vat}
                  value={row.vatId}
                  options={vatOptions}
                  onChange={(value) => updateRow(row.key, { vatId: value })}
                  disabled={!!row.processedOn}
                  fullWidth
                />
              </Box>
              <Box sx={{ width: hasMobileView ? undefined : 165, flexShrink: 0 }}>
                <DatePicker
                  label={translations.invoiceMonth}
                  value={row.invoiceMonth}
                  onChange={(value) => updateRow(row.key, { invoiceMonth: value })}
                  disabled={!!row.processedOn}
                  disabledEmpty
                  views={['month', 'year']}
                  fullWidth
                />
              </Box>
              <Box
                sx={{
                  width: hasMobileView ? undefined : 82,
                  flexShrink: 0,
                  display: 'flex',
                  flexDirection: hasMobileView ? 'row' : 'column',
                  alignItems: 'center',
                  gap: hasMobileView ? 1 : 0,
                  textAlign: 'center',
                }}
              >
                <Text.Small color="gray">{translations.countsAsRevenue}</Text.Small>
                <Checkbox
                  checked={row.countsAsRevenue}
                  onChange={({ target }) => updateRow(row.key, { countsAsRevenue: target.checked })}
                  disabled={!!row.processedOn}
                  size="small"
                />
              </Box>
              <Box sx={{ flexShrink: 0 }}>
                {row.processedOn ? (
                  <Text.Small color="gray">
                    {`${translations.invoiced} ${formatDate({
                      date: row.processedOn,
                      variant: 'dayMonthYear',
                    })}`}
                  </Text.Small>
                ) : (
                  <SVGButton
                    svgName="delete"
                    color="error"
                    size="small"
                    onClick={() => removeRow(row.key)}
                  />
                )}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <SectionSaveFooter
        dirty={hasChanges}
        saving={saving}
        error={missingFields ? translations.itemFieldsRequired : undefined}
        disabled={missingFields}
        onSave={save}
      />
    </Box>
  );
}

export { OrganisationScheduledItems };
